import { Injectable, ElementRef, Injector } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import * as CONST from '../app.const';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { QuickActionsComponent } from './quick-actions/quick-actions.component';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { QUICK_ACTIONS_OVERLAY, QUICK_ACTIONS_STRUCT } from './quick-actions/quick-actions.const';
import { ApiRequestService } from './api-request.service';
import { IndustriesModel } from "../admin/industries/industries.model";
import { TradeModel } from "../admin/trades/trades.model";
import { TaskAnalysesRevisedWorkflowModel } from "../models/task-analyses-revised-workflow.model";

// Import the default language file. It is replaced when the app is compiled.
import * as _ from 'lodash'; // Lodash is used to dynamically access properties of the default language file.
import { getLangTerm as _getLangTermFn } from './lang-utils';
import { HttpParams } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';

declare var $;

/**
 * Simplified Google Places autocomplete prediction. `label` is the human-readable prediction text
 * shown in the dropdown; `placePrediction` is the underlying Google object used to resolve details.
 */
export interface GooglePlacePrediction {
  label: string;
  placePrediction: any;
}

/**
 * Resolved Google Place details for a selected prediction.
 */
export interface GooglePlaceDetails {
  formattedAddress: string;
  location: any | null;
  addressComponents?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class UtilsService {

  toastClass = '';

  toastMessage = '';

  modalTitle = '';
  modalContent = '';
  modalOkBtn: () => void;

  // Cached promise for the Google Maps JavaScript API load. Ensures the API is bootstrapped
  // once per app session. Reset to null on failure so a later attempt can retry.
  private googleMapsLoadPromise: Promise<void> | null = null;

  constructor(
    public dialog: MatDialog,
    private overlay: Overlay,
    private injector: Injector,
    private router: Router,
    private location: Location,
    private api: ApiRequestService,
    private sanitizer: DomSanitizer
  ) { }

  /**
   * Show toast messages for 5 seconds at the bottom of the screen.
   *
   * @param message The message to be displayed.
   */
  showToast(message: string): void {
    // update the message
    this.toastMessage = message;
    // show the toast
    this.toastClass = 'show';
    // wait and dismiss
    setTimeout(() => {
      this.toastClass = '';
    }, 5000);
  }

  /**
   * Shows a modal with a title, content and optionally ok button.
   *
   * @param title
   * @param content Body text of the modal.
   * @param okBtn Function which runs on pressing the 'OK' button.
   *
   */
  showModal(title: string, content: string, okBtn?: () => void): void {
    // set the modal data
    this.modalTitle = title;
    this.modalContent = content;
    this.modalOkBtn = okBtn;

    try {
      // Causing an error in IE/Edge Legacy
      $('.modal').modal({
        backdrop: 'static'
      });
    }
    catch (err) {
      // IE/Edge Legacy Fix
      const userAgent = navigator.userAgent;
      if (userAgent.indexOf('MSIE ') > -1 || userAgent.indexOf('Trident/') > -1 || userAgent.indexOf('Edge/') > -1) {
        this.modalOkBtn = () => {
          $('.modal').hide();
        };
        $('.modal').show();
      }
    }
    finally {
      // optional
    }
  }

  showFormValidationError(additionalMessage: string = '') {
    this.showModal('Validation Failed', '<p>Please make sure you enter all the required fields denoted with an asterisk *. </p>' + additionalMessage);
  }

  /**
   * Shows a Material Design Dialog.
   *
   * @param component The Angular Component to be shown.
   * @param [data] Data to be passed to the Component as MAT_DIALOG_DATA.
   * @param [dialogOptions]
   * @param [callback] Function which runs after dialog closes.
   */
  showComponentDialog(
    component: any,
    data: any = {},
    dialogOptions: MatDialogConfig = {},
    callback?: any
  ): Promise<any> {
    if (!dialogOptions) {
      dialogOptions = {
        width: '1024px',
        // height: '600px',
        // maxHeight: '600px',
        data: data,
        autoFocus: false
        // closeOnNavigation: false,
      };
    } else {
      if (!dialogOptions['width']) {
        dialogOptions['width'] = '1024px';
      }

      dialogOptions['data'] = data;
      dialogOptions['autoFocus'] = false;
    }

    const dialog = this.dialog.open(component, dialogOptions);

    return dialog.afterClosed().toPromise().then((result) => {
      if (typeof callback === 'function') {
        callback(result);
      }
      return result;
    });
  }

  showQuickActions(elementRef: ElementRef, text: string, buttons: { text: string, handler: any }[] = []) {
    // Create the overlay
    const overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position()
        .flexibleConnectedTo(elementRef)
        .withPositions([{
          overlayX: 'start',
          overlayY: 'top',
          originX: 'start',
          originY: 'bottom'
        }]),
      hasBackdrop: true, // Enable the backdrop.
      backdropClass: 'cdk-overlay-transparent-backdrop' // Use a transparent backdrop.
    });

    // Create the injector tokens to pass data into the component portal.
    const injectorTokens = new WeakMap();
    injectorTokens.set(QUICK_ACTIONS_OVERLAY, overlayRef);
    injectorTokens.set(QUICK_ACTIONS_STRUCT, {
      text: text,
      buttons: buttons
    });
    const portalInjector = new PortalInjector(this.injector, injectorTokens);

    // Create the component portal that will automatically dismiss when the backdrop is clicked.
    const quickActionsComponentPortal = new ComponentPortal(QuickActionsComponent, null, portalInjector);
    overlayRef.attach(quickActionsComponentPortal);
    overlayRef.backdropClick().subscribe(() => overlayRef.dispose());
  }

  /**
   * Get a "likelihood" list for hazard risk assessments.
   * @returns
   */
  getRiskAssessmentLikelihoodList() {
    return [
      this.getLangTerm('hazards.form.risk_assessments.likelihood[0]', 'Rare'),
      this.getLangTerm('hazards.form.risk_assessments.likelihood[1]', 'Unlikely'),
      this.getLangTerm('hazards.form.risk_assessments.likelihood[2]', 'Possible'),
      this.getLangTerm('hazards.form.risk_assessments.likelihood[3]', 'Likely'),
      this.getLangTerm('hazards.form.risk_assessments.likelihood[4]', 'Almost Certain')
    ];
  }

  /**
   * Get a "severity" list for hazard risk assessments.
   * @returns
   */
  getRiskAssessmentSeverityList() {
    return [
      this.getLangTerm('hazards.form.risk_assessments.severity[0]', 'Insignificant'),
      this.getLangTerm('hazards.form.risk_assessments.severity[1]', 'Minor'),
      this.getLangTerm('hazards.form.risk_assessments.severity[2]', 'Moderate'),
      this.getLangTerm('hazards.form.risk_assessments.severity[3]', 'Major'),
      this.getLangTerm('hazards.form.risk_assessments.severity[4]', 'Catastrophic')
    ];
  }

  /**
   * Gets risk assessment text
   * @param likelihood The IRA likelihood of a Hazard|Substance.
   * @param severity The IRA severity of a Hazard|Substance.
   * @example
   * > getRiskAssessmentText(1, 2);
   * < "Low"
   */
  getRiskAssessmentText(likelihood: number, severity: number): string {
    const weight: number = likelihood * severity;

    if ([1, 2, 3, 4].includes(weight)) {
      return 'Low';
    }

    if ([5, 6, 7, 8, 9].includes(weight)) {
      return 'Moderate';
    }

    if ([10, 11, 12, 13, 14, 15, 16].includes(weight)) {
      return 'High';
    }

    if ([17, 18, 19, 20, 21, 22, 23, 24, 25].includes(weight)) {
      return 'Extreme';
    }

    return 'Not Set';
  }

  getRiskAssessmentBootstrapColor(likelihood: number, severity: number) {

    const riskAssessment = this.getRiskAssessmentText(likelihood, severity);

    if (['Low'].find((value) => value === riskAssessment)) {
      return 'success';
    }

    if (['Moderate'].find((value) => value === riskAssessment)) {
      return 'warning';
    }

    if (['High', 'Extreme'].find((value) => value === riskAssessment)) {
      return 'danger';
    }

    return 'danger';
  }

  calculateRiskScore(likelihood: number, severity: number) {
    return likelihood * severity;
  }

  /**
   * @returns [0, 1, 2, ..., 23]
   */
  getHoursInDay(): number[] {
    const hours = [];
    for (let hour = 0; hour <= 23; hour++) {
      hours.push(hour); // < 10 ? '0'+hour : hour
    }
    return hours;
  }

  /**
   * @returns [0, 1, 2, ..., 59]
   */
  getMinutesInHour(): number[] {
    const minutes = [];
    for (let minute = 0; minute <= 59; minute++) {
      minutes.push(minute); // < 10 ? '0'+minute : minute
    }
    return minutes;
  }

  /**
   * Maps month number to short name.
   *
   * @param month Month number such that 0 = Jan, 1 = Feb, ..., 11 = Dec
   * @returns Short name of the month. E.g. Jan, Feb.
   * @example
   * > getShortMonth(2);
   * < 'Mar'
   */
  getShortMonth(month: number): string {
    return [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ][month];
  }

  /**
   * Converts Unix time into a readable format.
   *
   * @param value Unix time.
   * @returns String representation of a timestamp. E.g. "Aug 20, 2019 14:27".
   * @example
   * > convertDate(1554017707);
   * < "Mar 31, 2019 20:35"
   */
  convertDate(value: number): string {
    const date = new Date(value * 1000);

    return (
      this.getShortMonth(date.getMonth()) +
      ' ' +
      date.getDate() +
      ', ' +
      date.getFullYear() +
      ' ' +
      this.zerofy(date.getHours()) +
      ':' +
      this.zerofy(date.getMinutes())
    );
  }

  /**
   * Converts a datetime string in ISO format to a human readable format.
   *
   * @param isoDate Date string in ISO format.
   * @returns String representation of a timestamp. E.g. "Aug 20, 2019 14:27".
   * @example
   * > convertISODate('2019-08-20T02:31:49.876Z')
   * < "Aug 20, 2019 14:27"
   */
  convertISODate(isoDate: string): string {
    const date = new Date(isoDate);

    return (
      this.getShortMonth(date.getMonth()) +
      ' ' +
      date.getDate() +
      ', ' +
      date.getFullYear() +
      ' ' +
      this.zerofy(date.getHours()) +
      ':' +
      this.zerofy(date.getMinutes())
    );
  }

  /**
   * @example
   * > zerofy(10);
   * < 10
   */
  zerofy(number: number): number | string {
    return number < 10 ? '0' + number : number;
  }

  /**
   * Converts Unix timestamp to human readable format.
   *
   * @param value unix timestamp
   * @example
   * > unixToDate(1554017707);
   * < "Mar 31, 2019"
   */
  unixToDate(value: number): string {
    const date = new Date(value * 1000);

    return (
      this.getShortMonth(date.getMonth()) +
      ' ' +
      date.getDate() +
      ', ' +
      date.getFullYear()
    );
  }

  /**
   *
   * @param dateString
   * convert a date string to unix timestamp.
   * e.g. "Feb 13, 2019 9:4" > 1550001840
   */
  dateStringToUnix(dateString: string): number {
    const date = Date.parse(dateString);
    return date / 1000;
  }

  /**
   * Converts seconds into a more readable format.
   *
   * @param elapsedTime in seconds
   * @example
   * > unixSecondsToElapsedDateTime(333);
   * < "6m 33s "
   */
  unixSecondsToElapsedDateTime(elapsedTime: number): string {
    const seconds: number = elapsedTime % 60; // remaining seconds
    let minutes: number = elapsedTime / 60; // minutes
    let hours: number = minutes / 60; // hours
    const days: number = hours / 24; // days

    minutes = minutes % 60; // reassign minutes
    hours = hours % 24; // reassign hours

    return (
      (days >= 1 ? Math.round(days) + 'd ' : '') +
      (hours >= 1 ? Math.round(hours) + 'h ' : '') +
      (minutes >= 1 ? Math.round(minutes) + 'm ' : '') +
      (seconds >= 1 ? Math.round(seconds) + 's ' : '')
    );
  }

  /**
   * Get the current environment as defined in app.const.ts
   * @example
   * > getEnvModeAsText()
   * < "LOCAL"
   */
  getEnvModeAsText(): string {
    return CONST.getEnvModeAsText();
  }

  /**
   * Retrieves the current version of the application.
   *
   * @return {string} The application version as a string.
   */
  getAppVersion(): string {
    return CONST.getAppVersion();
  }

  /**
   * Check if this is the production environment.
   */
  isProduction(): boolean {
    return CONST.isProduction();
  }

  /**
   * Get the default currency code (ISO 4217) for the current deployment.
   * Region-based: GBP for UK, NZD for ANZ. Defined in the environment files.
   */
  getDefaultCurrencyCode(): string {
    return CONST.getDefaultCurrencyCode();
  }

  /**
   * Filters an array of objects by unique ID.
   * @returns Entities with distinct IDs.
   * @example
   * > JSON.stringify(unique([{id: 1}, {id: 1}, {id: 2}]))
   * < '[{"id":1},{"id":2}]'
   */
  unique(entities: any[]): any[] {
    const result = [];
    const map = new Map();
    for (const entity of entities) {
      if (!map.has(entity.id)) {
        map.set(entity.id, true); // set any value to Map
        result.push(entity);
      }
    }
    return result;
  }

  /**
   * Converts a File object into a base64 string.
   */
  getBase64(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  async getIndustries(): Promise<IndustriesModel[]> {
    return await new Promise((resolve, reject) => {
      this.api.makeRequest('get', `v2/industries`, {}, {
        order_by: 'name',
        order: 'asc'
      })
        .then((response: { data: IndustriesModel[] }) => {
          resolve(response.data);
        })
        .catch((_errorResponse) => {
          this.handleAPIErrors(_errorResponse);
          reject([]);
        });
    });
  }

  async getTrades(): Promise<TradeModel[]> {
    return await new Promise((resolve, reject) => {
      this.api.makeRequest('get', `v2/trades`, {}, {
        order_by: 'name',
        order: 'asc'
      })
        .then((response: { data: TradeModel[] }) => {
          resolve(response.data);
        })
        .catch((_errorResponse) => {
          this.handleAPIErrors(_errorResponse);
          reject([]);
        });
    });
  }

  async getWorkflowSteps(ta_revised_id: number, site_id: number = 0): Promise<TaskAnalysesRevisedWorkflowModel[]> {
    return await new Promise((resolve, reject) => {

      if(site_id > 0) {
        this.api.makeRequest('get', `v2/task-analyses-revised/${ta_revised_id}/workflows`, {}, {
          order_by: 'step_no',
          order: 'asc',
          site_id: site_id,
          all_records: true
        })
          .then((response: { data: TaskAnalysesRevisedWorkflowModel[] }) => {
            resolve(response.data);
          })
          .catch((_errorResponse) => {
            this.handleAPIErrors(_errorResponse);
            reject([]);
          });
      } else {
        this.api.makeRequest('get', `v2/task-analyses-revised/${ta_revised_id}/workflows`, {}, {
          order_by: 'step_no',
          order: 'asc',
          all_records: true
        })
          .then((response: { data: TaskAnalysesRevisedWorkflowModel[] }) => {
            resolve(response.data);
          })
          .catch((_errorResponse) => {
            this.handleAPIErrors(_errorResponse);
            reject([]);
          });
      }

    });
  }

  /**
   * Use this to refresh the page or navigate to a different page.
   * This is typically used with edit pages that redirects the user to edit a newly created record.
   *
   * @param redirectTo The path to redirect to, broken up in string array pieces. You can omit this to just refresh the current page.
   * @param queryParams The query params to pass along with the redirect.
   */
  refreshPage(redirectTo?: any[], queryParams: any = {}) {
    // Redirect the user.
    return this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.redirectPage(redirectTo || [this.location.path()], queryParams);
    });
  }

  /**
   * Use this if you need to redirect the user to a different page.
   * Auto redirect is only used by the SigninComponent.
   *
   * @param redirectTo The path to redirect to, broken up in string array pieces.
   * @param queryParams The query params to pass along with the redirect.
   * @param includeAutoRedirect Include the current url as the redirect path if the user should be returned to the currently viewed path. This is typically used if the user is unauthenticated.
   */
  redirectPage(redirectTo?: any[], queryParams: any = {}, includeAutoRedirect: boolean = false) {
    // Automatically redirect the user back to where they were.
    if ( includeAutoRedirect ) {
      // Get the full relative path from the browser e.g. /feature?object=x&id=y
      const path = this.location.path();
      // Split the paths into two parts. The navigated route and query params.
      const path_parts = path.split('?'); // Query params starts with ? and is concatenated with &.
      // Set the redirect path.
      queryParams.redirect = path_parts[0];
      // Let's extract the query params.
      if ( typeof path_parts[1] != 'undefined' && path_parts[1] ) {
        // Split the query params with &. It will contain an array of "key=value" pairs.
        const query_params: string[] = path_parts[1].split('&');
        // If we have query params available, split the key and value from each other.
        if ( query_params.length > 0 ) {
          // Loop through all "key=value" pairs and split their key value values.
          for( let i = 0; i < query_params.length; i++ ) {
            // Split key value pairs with =.
            const query_param_set = query_params[i].split('=');
            // Check if the key is present. It can be preserved with an empty value.
            if ( typeof query_param_set[0] != 'undefined' && query_param_set[0] ) {
              // Store the key and value data in the queryParams array for preservation. It is ok to have empty values.
              queryParams[query_param_set[0]] = (typeof query_param_set[1] != 'undefined') && query_param_set[1] ? query_param_set[1] : '';
            }
          }
        }
      }
    }
    // Redirect the user.
    return this.router.navigate(redirectTo, {
      queryParams: queryParams
    });
  }

  /**
   * This can be used to redirect the user back to the page that was previously active.
   */
  goBack() {
    // Redirect the user.
    this.location.back();
  }

  /**
   * Removes a query param from the browser location's query param list.
   * @param query_param_key The query param to remove.
   * Credit: https://stackoverflow.com/a/70149164/10688654
   */
  removeBrowserLocationQueryParam(query_param_key: string = '') {
    const [path, query] = this.location.path().split('?');
    const params = new HttpParams({ fromString: query });
    this.location.replaceState(path, params.delete(query_param_key).toString());
  }

  getUserRoles() {
    return [
      'Administrator',
      'Employee'
    ];
  }

  /**
   * Handles API errors by extracting and displaying error messages.
   *
   * @param {any} errorResponse - The error response from the API.
   * @param returnErrorMessage
   * @return {void} - This method does not return anything.
   */
  handleAPIErrors(errorResponse: any, returnErrorMessage: boolean = false): string {
    // Define a basic error message variable.
    let errorMessage: string = '';
    // Check if the error response contains any errors.
    if ( errorResponse.error && errorResponse.error.errors ) {
      // Loop and extract all errors.
      Object.keys(errorResponse.error.errors).forEach((errorField: string): void => {
        Object.keys(errorResponse.error.errors[errorField]).forEach((errorEntry: string): void => {
          errorMessage += '<li>' + errorResponse.error.errors[errorField][errorEntry] + '</li>';
        });
      });
    }
    // Define a base error message.
    let baseMessage: string = '';
    // Check if an error message were specified.
    if ( errorResponse.error && errorResponse.error.message ) {
      baseMessage += errorResponse.error.message;
    } else {
      // Check the status text.
      if ( errorResponse.statusText ) {
        baseMessage += errorResponse.statusText;
      } else {
        // Check the base error message. It may include API controller names.
        if ( errorResponse.message ) {
          baseMessage += errorResponse.message;
        }
      }
    }
    // Add all errors to the basic error message.
    errorMessage = '<b>' + baseMessage + '</b><br><br><ul>' + errorMessage + '</ul>';

    if ( returnErrorMessage ) {
      return this.stripHtmlTags(errorMessage);
    }

    // Show the error message in a popup dialog.
    this.showModal('Error Message', errorMessage);
  }

  /**
   * Remove HTML tags from a given text string
   *
   * @param {string} text - The text from which HTML tags should be stripped
   * @return {string} The text string with HTML tags removed
   */
  stripHtmlTags(text: string): string {
    return text.replace(/<[^>]*>/g, '');
  }

  /**
   * Opens the provided URL in a new tab.
   * @param url The URL to open in a new tab.
   */
  openUrl(url: string): void {
    // Validate using the browser's native URL parser. This reliably handles
    // ports, paths, and query strings (the regex validator rejects URLs with a
    // port, such as http://localhost:4200/..., which silently broke this).
    try {
      const parsed: URL = new URL(url);

      // Only open http(s) links in a new tab.
      if ( parsed.protocol === 'http:' || parsed.protocol === 'https:' ) {
        window.open(parsed.href, '_blank');
        return;
      }

      console.log('Please provide a valid http(s) URL.');
    } catch {
      console.log('Please provide a valid URL.');
    }
  }

  /**
   * Downloads the provided URL to the browser.
   * @param url The URL to download.
   */
  downloadUrl(url: string) {
    // TBD: Add code to download the provided file.
    // Experimental Code:
    window.location.assign(url);
    // Another Solution:
    // let downloadAnchor = document.createElement('a');
    // downloadAnchor.href = url;
    // downloadAnchor.download = url;
    // downloadAnchor.target = '_blank';
    // document.body.appendChild(downloadAnchor);
    // downloadAnchor.click();
    // document.body.removeChild(downloadAnchor);
  }

  /**
   * This returns a list of site statuses extracted form a JSON object language file.
   */
  getSiteTypes() {
    return this.getLangTerm('sites.list.site-types', []);
  }

  /**
   * This returns a list of site statuses extracted from a JSON object language file.
   */
  getSiteStatuses() {
    return this.getLangTerm('sites.list.site-statuses', []);
  }

  getSiteRiskStatusTextColor(site_risk_status: string) {
    switch (site_risk_status.toLowerCase()) {
      case 'low':
        return 'text-green';
      case 'moderate':
        return 'text-light-orange';
      case 'high':
        return 'text-dark-orange';
      case 'extreme':
        return 'text-red';
      default:
        return '';
    }
  }

  getSiteRiskStatusChipColor(site_risk_status: string) {
    switch (site_risk_status.toLowerCase()) {
      case 'low':
        return 'chip-success';
      case 'moderate':
        return 'chip-warning';
      case 'high':
        return 'chip-warning-secondary';
      case 'extreme':
        return 'chip-danger';
      default:
        return '';
    }
  }

  getSiteTAStatusTextColor(site_ta_status: string) {
    switch (site_ta_status.toLowerCase()) {
      case 'acknowledged':
      case 'approved':
      case 'authorized':
        return 'text-green';
      case 'pending':
        return 'text-orange';
      case 'required':
        return 'text-red blink';
      default:
        return '';
    }
  }

  getSiteTAUserCheck(site_users: any, user_id: number) {
    let check = false;
    if (site_users && site_users.length > 0) {
      site_users.forEach((user, index) => {
        if (user.id == user_id) {
          check = true;
          return check;
        }
      });
    }

    return check;
  }

  getSiteTAUserStatus(site_users: any) {
    let status = true;
    if (site_users && site_users.length > 0) {
      site_users.forEach((user, index) => {
        if (user.pivot.acknowledged == 0) {
          status = false;
          return status;
        }
      });
    } else {
      status = false;
    }

    return status;
  }

  getSiteTAUserStatusRequired(site_users: any, user_id: number) {
    let required = false;
    if (site_users && site_users.length > 0) {
      site_users.forEach((user, index) => {
        if (user.pivot.acknowledged == 0 && user.id == user_id) {
          required = true;
          return required;
        }
      });
    }

    return required;
  }

  getSafetyObservationCategories() {
    return [
      'Safety Mentions',
      'Opportunities for Improvements',
      'Critical Items',
      'Safe Act',
      'Unsafe Act',
      'Unsafe Condition',
      'Safety Rule Violation'
    ];
  }

  /**
   * Retrieves the list of safety observation types.
   *
   * @returns {string[]} An array containing safety observation types.
   *
   * API: app/Services/SafetyObservationsService.php
   * Mobile App: src/app/services/utils.service.ts
   */
  getSafetyObservationTypes(): string[] {
    return [
      'Access',
      'Accessibility',
      'Accommodation',
      'Electrical Hazard',
      'Environmental',
      'Excavation',
      'Fire Safety Equipment and Procedures',
      'General Equipment',
      'Lighting',
      'Non-compliance',
      'Personal Protective Equipment',
      'Public Safety',
      'Security',
      'Storage / Containment',
      'Tidiness / Cleanliness',
      'Traffic Management',
      'Transport',
      'Vehicles',
      'Work Environment',
      'Working at Heights',
      'Other'
    ];
  }

  getSafetyObservationPriorities() {
    return [
      'Not Applicable',
      'Very Low',
      'Low',
      'Moderate',
      'High',
      'Critical'
    ];
  }

  getSafetyObservationStatuses() {
    return [
      'Open',
      'Review in Progress',
      'Closed: No Action',
      'Closed: With Action',
      'Archived'
    ];
  }

  getSafetyObservationStatusCssClasses(so_status: string) {
    switch (so_status) {
      case 'Open':
        return 'text-red blink';
      case 'Review in Progress':
        return 'text-blue';
      case 'Closed: No Action':
      case 'Closed: With Action':
      case 'Archived':
        return 'text-green';
      default:
        return '';
    }
  }

  /**
   * Get a list of priorities for corrective actions.
   * This needs to be aligned with other risk levels.
   * @returns an array of priorities for corrective actions.
   */
  getCorrectiveActionPriorities() {
    return [
      'Low',
      'Medium',
      'High',
      'Critical'
    ];
  }

  /**
   * Get a list of types for actions.
   * @returns an array of types for actions.
   */
  getActionTypes() {
    return [
      'Corrective Action',
      'Compliance & Regulatory Inspections',
      'Contractor Management & Performance',
      'Cybersecurity & Data Protection',
      'Electrical Systems',
      'Emergency Exits & Evacuation Routes',
      'Environmental Management',
      'Ergonomics & Manual Handling',
      'Explosive Handling & Storage',
      'Fire Safety Equipment',
      'Food Safety & Hygiene',
      'General Maintenance',
      'Hazardous Materials Handling',
      'Housekeeping & Cleanliness',
      'HVAC & Ventilation',
      'Induction & Onboarding',
      'Incident Investigation',
      'Inspections & Audits',
      'Lighting (General & Emergency)',
      'Machinery & Equipment Safety',
      'Maintenance & Repairs',
      'Noise & Vibration Hazards',
      'Pest Control',
      'Plumbing & Water Systems',
      'Procedure & Policy Updates',
      'Production & Quality Control',
      'Safety Barriers & Guarding',
      'Safety Equipment Checks',
      'Security Systems & Access Control',
      'Signage & Labelling',
      'Site & Logistics Preparation',
      'Structural Integrity',
      'Training & Competency',
      'Vehicle & Traffic Management',
      'Working at Heights',
      'Other'
    ];
  }

  /**
   * Get a list of non-system origins for actions.
   * @returns an array of non-system origins for actions.
   */
  getActionNonSystemOrigins() {
    return [
      'Internal Audits / Inspections',
      'External Audits / Regulatory Inspections',
      'Accidents / Notifiable Events',
      'Hazard / Near Miss Reporting',
      'Risk Assessments',
      'Equipment / Maintenance',
      'Management of Change (MOC)',
      'Emergency Preparedness / Drills',
      'Management Review Meetings',
      'Worker Participation / HSR Feedback',
      'Customer / Client Complaints',
      'Non-Conformance Reports',
      'Continuous Improvement Initiatives',
      'Task',
      'Other / General'
    ];
  }

  /**
   * Get a list of system origins for actions.
   * @returns an array of system origins for actions.
   */
  getActionSystemOrigins() {
    return [
      "Asset",
      "Inspection & Audit",
      "Incident",
      "Safety Observation",
      "Toolbox Talk"
    ];
  }

  /**
   * Check if the provided URL links to an image.
   * @param url The URL of the file to check.
   */
  isImage(url: string) {
    return url && url.match(/.(jpg|jpeg|png|gif|bmp)/i);
  }

  /**
   * Convert JSON string to JSON object.
   * @param jsonString The value of the json string to convert.
   */
  jsonParse(jsonString: any) {
    return JSON.parse(jsonString);
  }

  /**
   * Instance language term resolver for use in templates via utils.getLangTerm().
   * Delegates to the standalone getLangTerm function in lang-utils.ts.
   */
  getLangTerm(path: string, defaultValue: any) {
    return _getLangTermFn(path, defaultValue);
  }

  /**
   * Converts a given string into a URL-friendly format by transforming it into kebab-case.
   *
   * @param {string} [value=''] - The input string to be converted. Defaults to an empty string if no value is provided.
   * @return {string} The URL-friendly, kebab-cased version of the input string.
   */
  urlFriendlyString(value: string = ''): string {
    return _.kebabCase(value);
  }

  // Get current timezone of location
  getCurrentTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Get the URL of the knowledgebase.
   * @returns The URL to the knowledgebase.
   */
  getKnowledgebaseUrl() {
    return CONST.getKnowledgebaseUrl();
  }

  /**
   * Get the URL of the release notes for the software.
   * @returns The URL to the release notes.
   */
  getReleaseNotesUrl() {
    return CONST.getReleaseNotesUrl();
  }

  /**
   * Get the name of the app.
   * @returns The name of the app.
   */
  getAppName() {
    return CONST.getAppName();
  }

  /**
   * Get the URL to the mobile app in the app store for Android.
   * @returns The Android mobile app store URL.
   */
  getAndroidMobileAppStoreUrl() {
    return CONST.getAndroidMobileAppStoreUrl();
  }

  /**
   * Get the URL to the kiosk app in the app store for Android.
   * @returns The Android kiosk app store URL.
   */
  getAndroidKioskAppStoreUrl() {
    return CONST.getAndroidKioskAppStoreUrl();
  }

  /**
   * Get the URL to the mobile app in the app store for iOS.
   * @returns The iOS mobile app store URL.
   */
  getIosMobileAppStoreUrl() {
    return CONST.getIosMobileAppStoreUrl();
  }

  /**
   * Get the URL to the kiosk app in the app store for iOS.
   * @returns The iOS kiosk app store URL.
   */
  getIosKioskAppStoreUrl() {
    return CONST.getIosKioskAppStoreUrl();
  }

  /**
   * Get the URL to the privacy policy from the environment file.
   */
  getPrivacyPolicyUrl() {
    return CONST.getPrivacyPolicyUrl();
  }

  /**
   * Get the URL to the terms and conditions from the environment file.
   */
  getTermsAndConditionsUrl() {
    return CONST.getTermsAndConditionsUrl();
  }

  /**
   * Get the sales email from the environment files.
   * @returns The sales email address.
   */
  getSalesEmailAddress() {
    return CONST.getSalesEmailAddress();
  }

  /**
   * Get the support email from the environment files.
   * @returns The support email address.
   */
  getSupportEmailAddress() {
    return CONST.getSupportEmailAddress();
  }

  getCompanyAddress() {
    return CONST.getCompanyAddress();
  }

  getSalesPhone() {
    return CONST.getSalesPhone();
  }

  showMap() {
    return CONST.showMap();
  }

  getMapIframeUrl() {
    return this.sanitizer.bypassSecurityTrustResourceUrl(CONST.getMapIframeUrl());
  }

  /**
   * Retrieves the Maps API key used for accessing mapping services.
   *
   * @return {string} The Maps API key as a string.
   */
  getGoogleMapsAPIKey(): string {
    return CONST.getGoogleMapsAPIKey();
  }

  /**
   * Loads the Google Maps JavaScript API using Google's official inline bootstrap loader
   * and the dynamic library import (importLibrary) mechanism.
   *
   * This replaces the legacy direct-script approach (maps/api/js?libraries=...&callback + setTimeout),
   * which is incompatible with the current Maps JavaScript API for two reasons:
   *   1. Sub-libraries are now bootstrapped asynchronously, so touching google.maps.* after a fixed
   *      delay raced against library readiness.
   *   2. The Drawing Library (google.maps.drawing.DrawingManager) was decommissioned as of v3.65
   *      (May 2026) and is no longer part of the API at all. The geofence editor no longer depends
   *      on it (polygon drawing is implemented with core map-click + editable Polygon instead).
   *
   * Every library still used across the portal is awaited up front. Because the bootstrap loader keeps
   * populating the global google.maps.* namespace, existing google.maps.* call sites continue to work
   * once this promise resolves.
   *
   * The returned promise is cached, so the API is only bootstrapped once per app session. If the load
   * fails, the cached promise is cleared so a later attempt can retry.
   *
   * @returns {Promise<void>} Resolves once all required Google Maps libraries are ready.
   */
  public loadGoogleMaps(): Promise<void> {
    // Return the in-flight or completed load if it has already started.
    if ( this.googleMapsLoadPromise ) {
      return this.googleMapsLoadPromise;
    }

    const googleMapsAPIKey: string = this.getGoogleMapsAPIKey();
    if ( !googleMapsAPIKey ) {
      return Promise.reject(new Error('Google Maps API key is not configured.'));
    }

    this.googleMapsLoadPromise = (async (): Promise<void> => {
      // Install Google's inline bootstrap loader (defines google.maps.importLibrary). No-op if present.
      this.installGoogleMapsBootstrap(googleMapsAPIKey);

      const googleNamespace: any = (window as any).google;

      // Await every library the portal uses. The Drawing Library is intentionally NOT requested:
      // it was decommissioned from the Maps JavaScript API as of v3.65. importLibrary resolves with
      // the library object and also keeps the global google.maps.* namespaces populated.
      const libraries: any[] = await Promise.all([
        googleNamespace.maps.importLibrary('core'),
        googleNamespace.maps.importLibrary('maps'),
        googleNamespace.maps.importLibrary('places'),
        googleNamespace.maps.importLibrary('geometry'),
        googleNamespace.maps.importLibrary('geocoding'),
        googleNamespace.maps.importLibrary('marker')
      ]);

      const placesLib: any = libraries[2];
      const geometryLib: any = libraries[3];

      // Back-fill the legacy sub-namespaces from the resolved library objects so existing call sites
      // (google.maps.places.*, google.maps.geometry.*) keep working even if a future API change stops
      // auto-populating them. Geocoder and Marker are attached directly onto google.maps.
      googleNamespace.maps.places = googleNamespace.maps.places || placesLib;
      googleNamespace.maps.geometry = googleNamespace.maps.geometry || geometryLib;
    })().catch((error) => {
      // Clear the cached promise so a future call can retry the load.
      this.googleMapsLoadPromise = null;
      // Surface the real failure for diagnostics (API not enabled, key restriction, library load, etc.).
      console.error('Google Maps failed to load:', error);
      throw error;
    });

    return this.googleMapsLoadPromise;
  }

  /**
   * Installs Google's official inline bootstrap loader for the Maps JavaScript API.
   * This defines window.google.maps.importLibrary, which lazily loads the underlying script the
   * first time a library is requested. It is a no-op if the loader (or a previously loaded Maps
   * script) is already present.
   *
   * The loader body is Google's published snippet, injected verbatim as inline script text so its
   * microtask batching behaviour (collecting all importLibrary calls made in the same tick into a
   * single request) is preserved exactly. Only the API key and version channel are substituted.
   *
   * @param {string} apiKey The Google Maps API key.
   */
  private installGoogleMapsBootstrap(apiKey: string): void {
    const win: any = window;

    // Already installed by the bootstrap or a previously loaded Maps script.
    if ( win.google && win.google.maps && win.google.maps.importLibrary ) {
      return;
    }

    // Guard against injecting the bootstrap script element more than once.
    if ( document.getElementById('google-maps-bootstrap') ) {
      return;
    }

    // Google's official inline bootstrap loader. The snippet uses only double quotes and backticks
    // internally, so it is safe to hold in a single-quoted string without escaping.
    const loaderBody: string =
      '(g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.${c}apis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({' +
      'key:' + JSON.stringify(apiKey) + ',' +
      'v:"weekly"' +
      '});';

    const script: HTMLScriptElement = document.createElement('script');
    script.id = 'google-maps-bootstrap';
    script.type = 'text/javascript';
    script.text = loaderBody;
    document.head.appendChild(script);
  }

  /**
   * Create a new Places autocomplete session token.
   *
   * Session tokens group the typing phase and the final place-details fetch into one billable
   * session. Create a fresh token when the user starts a new search, pass it to every
   * getPlacePredictions() call in that search, and discard it after getPlaceDetails() resolves
   * (which ends the session).
   *
   * @returns {Promise<any>} Resolves with a new google.maps.places.AutocompleteSessionToken.
   */
  public async createPlacesSessionToken(): Promise<any> {
    await this.loadGoogleMaps();
    const placesLib: any = await (window as any).google.maps.importLibrary('places');
    return new placesLib.AutocompleteSessionToken();
  }

  /**
   * Fetch Google Places autocomplete predictions for a query string using the Places Data API
   * (AutocompleteSuggestion.fetchAutocompleteSuggestions). This replaces the deprecated
   * google.maps.places.Autocomplete widget.
   *
   * @param {string} input The user's query text.
   * @param {any} sessionToken A session token from createPlacesSessionToken() for billing grouping.
   * @param {any} [locationBias] Optional google.maps.LatLngBounds (or bounds literal) to bias results.
   * @returns {Promise<GooglePlacePrediction[]>} Resolves with simplified predictions (id + label).
   */
  public async getPlacePredictions(input: string, sessionToken: any, locationBias?: any): Promise<GooglePlacePrediction[]> {
    // Nothing to search for.
    if (!input || !input.trim()) {
      return [];
    }

    await this.loadGoogleMaps();
    const placesLib: any = await (window as any).google.maps.importLibrary('places');

    const request: any = {
      input: input,
      sessionToken: sessionToken,
      // Region-bias predictions to the configured default country (e.g. NZ, GB).
      region: CONST.getDefaultCountryCode()
    };

    // Bias (not restrict) toward the current map viewport when one is supplied.
    if (locationBias) {
      request.locationBias = locationBias;
    }

    const response: any = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    const suggestions: any[] = (response && response.suggestions) ? response.suggestions : [];

    // Map to a simplified, framework-agnostic shape. Keep the underlying placePrediction so the
    // caller can resolve full place details on selection.
    return suggestions
      .filter((suggestion: any) => !!suggestion.placePrediction)
      .map((suggestion: any): GooglePlacePrediction => ({
        label: suggestion.placePrediction.text ? suggestion.placePrediction.text.toString() : '',
        placePrediction: suggestion.placePrediction
      }));
  }

  /**
   * Resolve the full place details for a selected prediction. Calling this ends the autocomplete
   * session associated with the prediction's session token.
   *
   * @param {any} placePrediction A placePrediction from a GooglePlacePrediction returned above.
   * @returns {Promise<GooglePlaceDetails>} Resolves with the formatted address and location.
   */
  public async getPlaceDetails(placePrediction: any): Promise<GooglePlaceDetails> {
    const place: any = placePrediction.toPlace();
    await place.fetchFields({
      fields: ['formattedAddress', 'location', 'addressComponents']
    });

    return {
      formattedAddress: place.formattedAddress || '',
      location: place.location || null,
      addressComponents: place.addressComponents || []
    };
  }

  /**
   * Returns the upvoty link.
   *
   * @returns {string} The upvoty link.
   */
  getUpvotyLink(): string {
    return CONST.getUpvotyLink();
  }

  /**
   * Get the urgent notice URL from the environment files.
   */
  getUrgentNoticeUrl() {
    return CONST.getUrgentNoticeUrl();
  }

  /**
   * Retrieves the TinyMCE API key.
   *
   * @return {string} The TinyMCE API key retrieved from the CONST constant.
   */
  getTinyMceAPIKey(): string {
    return CONST.getTinyMceAPIKey();
  }

  /**
   * Prepares a site related link.This is primarily used when viewing or editing a site or related features.
   * @param site The site to create the link for.
   * @param action The action can be view or edit.
   * @param route The route sections that forms part of the route which is added at the tail end of the full route.
   */
  prepareSiteRelatedLink(id: number, parent_site_id: number, route: string) {
    // Prepare the base route.
    const route_base = parent_site_id ? `/sites/${parent_site_id}/children/${id}/${route}` : `/sites/${id}/${route}`;
    // Return the route.
    return route_base;
  }

  /**
   * Returns an array with Hazardous Substance types.
   */
  getHazardousSubstanceTypes() {
    return [
      'Gas',
      'Liquid',
      'Solid'
    ]
  }

  /**
   * Returns an array with measurement units.
   */
  getHazardousSubstanceUnitTypes() {
    return [
      'Kg',
      'L',
      'fl.oz',
      'gal.',
      'lb.',
      'oz.'
    ];
  }

  /**
   * Get the measurement unit based on the provided Hazardous Substance type.
   * @param hazardous_substance_type
   */
  getHazardousSubstanceUnitType(hazardous_substance_type: string) {
    // Set the measurement unit.
    let measurement_unit: string = '';
    // Find the measurement unit based on the Hazardous Substance type.
    switch ( hazardous_substance_type ) {
      case 'Solid':
        measurement_unit = 'Kg';
        break;
      case 'Liquid':
      case 'Gas':
        measurement_unit = 'L';
        break;
      default:
        // Do not change.
    }
    // Return the measurement unit.
    return measurement_unit;
  }

  /**
   * Use this method to validate a mobile number.
   * @param mobile The mobile number could have + prepended hence we are using strings.
   * @returns boolean True if the mobile number is valid. False if the mobile number is invalid.
   */
  isValidContactNumber(mobile: string) {
    // Regex to validate a mobile number.
    const regex = /^(\+?\d{1,3}[\s-]?)?\(?\d{1,4}\)?[\s-]?\d{1,4}[\s-]?\d{1,10}$/;
    // Return the result of the regex test.
    return regex.test(mobile);
  }

  /**
   * Use this method to validate an email address.
   * @param email The email address to validate.
   * @returns boolean True if the email address is valid. False if the email address is invalid.
   */
  isValidEmailAddress(email: string) {
    // Regex to validate an email address.
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Return the result of the regex test.
    return regex.test(email);
  }

  /**
   * Use this method to check if a URL is valid. URLs can both include and exclude the protocols.
   * @param {string} url The URL to validate.
   * @returns {boolean} True if the URL is valid. False if the URL is invalid.
   */
  isValidURL(url: string): boolean {
    // Regex to validate a URL.
    const urlRegex: RegExp = /^((https?|ftp):\/\/)?((?:[a-z0-9\-]+\.)+[a-z]{2,}|localhost)(\/[a-z0-9\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;
    // Return the result of the regex test.
    return urlRegex.test(url);
  }

  /**
   * Load an external script. This will automatically append the script to the body.
   * You can use the callback function if you need to implement features that depend on the script.
   * @param id The ID of the script to load.
   * @param url The URL of the script to load e.g. Google Maps.
   * @param callback The callback function to execute after the script has loaded.
   */
  public loadExternalScript(id: string, url: string, callback?: Function) {
    const scriptExists = document.getElementById(id);
    if ( !scriptExists ) {
      const body = <HTMLDivElement> document.body;
      const script = document.createElement('script');
      script.innerHTML = '';
      script.src = url;
      script.id = id;
      script.async = true;
      script.defer = true;
      body.appendChild(script);
      if ( callback ) {
        script.onload = () => {
          callback();
        };
      }
    } else {
      callback();
    }
  }

  /**
   * Retrieves a list of incident types.
   *
   * @return {string[]} - An array of incident types.
   */
  getIncidentTypes(): string[] {
    return this.getLangTerm('incidents.types', [
      'Environment',
      'Illness',
      'Injury',
      'Machinery',
      'Mobile Plant',
      'Motor Vehicle',
      'Near Miss',
      'Production Management',
      'Property Damage',
      'Security',
      'Other'
    ]);
  }

  /**
   * Incident types that capture injury details (injury type + affected body parts).
   * Single source of truth so the conditional fields stay consistent across the
   * create dialog, edit page and read-only view.
   */
  private readonly incidentInjuryDetailTypes: string[] = [
    'Injury',
    'Specified Injury',
    'Over-7-Day Injury',
    'Non-fatal Accident to Member of Public',
    'Fatality'
  ];

  /**
   * Subset of injury-detail types for which the injury type field is mandatory.
   * Currently mirrors incidentInjuryDetailTypes. To make a type's injury fields
   * optional later, remove it from this list (the field will still be shown).
   */
  private readonly incidentInjuryDetailRequiredTypes: string[] = [
    'Injury',
    'Specified Injury',
    'Over-7-Day Injury',
    'Non-fatal Accident to Member of Public',
    'Fatality'
  ];

  /** Incident types that capture illness details (illness type). */
  private readonly incidentIllnessDetailTypes: string[] = [
    'Illness',
    'Occupational Disease'
  ];

  /** Subset of illness-detail types for which the illness type field is mandatory. */
  private readonly incidentIllnessDetailRequiredTypes: string[] = [
    'Illness',
    'Occupational Disease'
  ];

  incidentTypeShowsInjuryDetails(type: string): boolean {
    return this.incidentInjuryDetailTypes.includes(type);
  }

  incidentTypeRequiresInjuryDetails(type: string): boolean {
    return this.incidentInjuryDetailRequiredTypes.includes(type);
  }

  incidentTypeShowsIllnessDetails(type: string): boolean {
    return this.incidentIllnessDetailTypes.includes(type);
  }

  incidentTypeRequiresIllnessDetails(type: string): boolean {
    return this.incidentIllnessDetailRequiredTypes.includes(type);
  }

  /**
   * Retrieves a list of incident statuses.
   *
   * @return {string[]} - An array of incident statuses.
   */
  getIncidentStatuses(): string[] {
    return [
      'Reported',
      'Assigned',
      'In Progress',
      'Submitted',
      'Completed'
    ];
  }

  /**
   * Normalizes query parameters.
   *
   * @param {Object} params - The query parameters to be normalized.
   * @return {Object} - The normalized query parameters.
   */
  normalizeQueryParams(params: { [key: string]: any }): { [key: string]: any } {
    return Object.entries(params).reduce((acc, [key, value]) => {
      acc[key] = Array.isArray(value) ? value.join(',') : value;
      return acc;
    }, {});
  }

  /**
   * Returns an array of document types for accounts.
   *
   * The list of document types must be included in the list in the API (App/Models/Folder::getDocumentTypes()).
   *
   * @return {string[]} An array of document types for accounts.
   */
  public getDocumentTypesForAccounts(): string[] {
    return [
      'Certificate',
      'Instructions',
      'Insurance',
      'Log',
      'Manual',
      'Meeting',
      'Minutes',
      'Permit',
      'Plan',
      'Policy',
      'Procedure',
      'Record',
      'Report',
      'Training',
      'Other'
    ];
  }

  /**
   * Retrieves the available document types for users.
   *
   * This method returns an array of document types that are available for users.
   * The list of document types must be included in the list in the API (App/Models/Folder::getDocumentTypes()).
   *
   * @returns {string[]} - An array of document types available for users.
   */
  public getDocumentTypesForUsers(): string[] {
    return [
      'Certificate',
      'CV',
      'Degree',
      'Diploma',
      'Drivers License',
      'External Training Record',
      'Forkhoist License',
      'In-house Training Record',
      'Induction',
      'License Other',
      'Passport',
      'Police Vetting',
      'Policy',
      'Procedure',
      'Work Health and Safety',
      'Work Visa',
      'Workers Compensation',
      'Other'
    ];
  }

  /**
   * Retrieves a list of maintenance and safety checks for tools.
   *
   * @returns {string[]} - Array containing the list of maintenance and safety checks.
   */
  getToolsMaintenanceAndSafetyChecksList(): string[] {
    return [
      'Routine Maintenance',
      'Scheduled Inspections',
      'Periodic Testing and Tagging',
      'Safety Audits',
      'Equipment Calibration',
      'Preventive Maintenance Checks',
      'Quality Control Checks',
      'Regulatory Compliance Checks',
      'Emergency Preparedness Assessments',
      'Asset Integrity Assessments',
      'Pressure Test',
      'Safety Harness/Lanyard inspection',
      'Fire Extinguisher Annual Service',
      'LPG Bottle inspection',
      'First Aid Kit inspection'
    ];
  }

  /**
   * Downloads a file from the specified URL. If provided, a custom file name can be assigned
   * for the downloaded file. Optionally, an event can be passed to suppress default event behavior.
   *
   * @param {string} url - The URL from which the file will be downloaded.
   * @param {string} [custom_file_name] - A custom name for the downloaded file (optional).
   * @param {Event} [event] - The event object to prevent default and stop propagation (optional).
   * @return {void} This method does not return any value.
   */
  onDownloadFromTemporaryURL(url: string, custom_file_name?: string, event?: Event): void {
    if ( event ) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.api.makeRequest('get', 'v2/file-manager/get-temporary-file-download-url', {}, {
      url: url,
      custom_file_name: custom_file_name
    })
      .then((response): void => {
        if ( !response.url ) {
          this.showToast('There was an error downloading the file. We did not get a URL back from the API.');
          return;
        }
        const a: HTMLAnchorElement = document.createElement('a');
        a.href = response.url;
        if ( response.custom_file_name ) {
          a.download = response.custom_file_name;
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })
      .catch((errorResponse) => {
        this.showModal('File Download Error', this.handleAPIErrors(errorResponse));
      });
  }

  /**
   * Retrieves a list of options representing the categories of individuals who might be involved in an incident.
   *
   * @return {string[]} An array of strings where each string identifies a category of potential individuals involved, such as 'Employee', 'Contractor', 'Visitor', etc.
   */
  getIncidentWhoWasInvolvedOptions(): string[] {
    return [
      'Employee', 'Contractor', 'Subcontractor', 'Visitor', 'Third-Party', 'Customer', 'Public', 'Unknown', 'Other'
    ];
  }
}
